import { ROSELoader } from './rose';
import { BinaryReader } from '../util/binaryreader';
import { BinaryWriter } from '../util/binarywriter';

const ROSE_POSITION_SCALE_IN = 0.01;
const ROSE_POSITION_SCALE_OUT = 100;

export interface RawIFOBlock {
  type: number;
  data: Uint8Array;
}

/**
 * @enum {Number}
 * @readonly
 */
export const BLOCK_TYPE = {
  INFO: 0,
  OBJECT: 1,
  NPC: 2,
  BUILDING: 3,
  SOUND: 4,
  EFFECT: 5,
  ANIMATION: 6,
  WATER_PATCH: 7,
  MONSTER_SPAWN: 8,
  WATER_PLANE: 9,
  WARP_POINT: 10,
  COLLISION_OBJECT: 11,
  EVENT_OBJECT: 12
};

export class MapObject {
  name: string = '';
  warpId: number = 0;
  eventId: number = 0;
  objectType: number = 0;
  objectId: number = 0;
  mapPosition: any = null;
  rotation: any = null;
  position: any = null;
  scale: any = null;
}

export class ZoneObject extends MapObject {}

export class ZoneNpc extends MapObject {
  aiIndex: number = 0;
  conFilePath: string = '';
}

export class ZoneBuilding extends MapObject {}

export class ZoneSound extends MapObject {
  filePath: string = '';
  range: number = 0;
  interval: number = 0;
}

export class ZoneEffect extends MapObject {
  filePath: string = '';
}

export class ZoneAnimation extends MapObject {}

export class WaterPatch {
  hasWater: boolean = false;
  height: number = 0;
  type: number = 0;
  id: number = 0;
  unknown: number = 0;
}

export class ZoneWaterPatches extends MapObject {
  width: number = 0;
  height: number = 0;
  patches: WaterPatch[] = [];
}

export class MonsterSpawnEntry {
  name: string = '';
  monster: number = 0;
  count: number = 0;
}

export class ZoneMonsterSpawn extends MapObject {
  name: string = '';
  normal: MonsterSpawnEntry[] = [];
  tactical: MonsterSpawnEntry[] = [];
  interval: number = 0;
  limit: number = 0;
  range: number = 0;
  tacticalPoints: number = 0;
}

export class ZoneWaterPlane {
  start: any = null;
  end: any = null;
}

export class ZoneWarpPoint extends MapObject {}

export class ZoneCollision extends MapObject {}

export class ZoneEvent extends MapObject {
  funcName: string = '';
  conFilePath: string = '';
}

/**
 * @property {ZoneObject[]} objects
 * @property {ZoneNpc[]} npcs
 * @property {ZoneBuilding[]} buildings
 * @property {ZoneSound[]} sounds
 * @property {ZoneEffect[]} effects
 * @property {ZoneAnimation[]} animations
 * @property {ZoneWaterPatches} waterPatches
 * @property {ZoneMonsterSpawn[]} monsterSpawns
 * @property {ZoneWaterPlane[]} waterPlanes
 * @property {ZoneWarpPoint[]} warps
 * @property {ZoneCollision[]} collisions
 * @property {ZoneEvent[]} events
 */
export class ZoneChunkData {
  // Static reference to enum for backwards compatibility
  static BLOCK_TYPE = BLOCK_TYPE;

  objects: ZoneObject[] = [];
  npcs: ZoneNpc[] = [];
  buildings: ZoneBuilding[] = [];
  sounds: ZoneSound[] = [];
  effects: ZoneEffect[] = [];
  animations: ZoneAnimation[] = [];
  waterPatches: ZoneWaterPatches | null = null;
  monsterSpawns: ZoneMonsterSpawn[] = [];
  waterPlanes: any[] = [];
  waterSize: number = 0;
  warps: ZoneWarpPoint[] = [];
  collisions: ZoneCollision[] = [];
  events: ZoneEvent[] = [];
  /** Original block ordering, retained so an unchanged file has stable structure. */
  sourceBlockOrder: number[] = [];
  /** INFO and unknown blocks that this implementation cannot interpret yet. */
  rawBlocks: RawIFOBlock[] = [];

  /**
   * @param {BinaryReader} rh
   * @param {Object} object
   * @returns {Object}
   */
  static loadMapObject(rh: BinaryReader, object: any) {
    object.name        = rh.readUint8Str();
    object.warpId      = rh.readUint16();
    object.eventId     = rh.readUint16();
    object.objectType  = rh.readUint32();
    object.objectId    = rh.readUint32();
    object.mapPosition = rh.readIntVector2();
    object.rotation    = rh.readQuat();
    object.position    = rh.readVector3().multiplyScalar(ROSE_POSITION_SCALE_IN);
    object.scale       = rh.readVector3();
    return object;
  }

  static writeMapObject(writer: BinaryWriter, object: MapObject): void {
    writer.writeUint8Str(object.name || '');
    writer.writeUint16(object.warpId || 0);
    writer.writeUint16(object.eventId || 0);
    writer.writeUint32(object.objectType || 0);
    writer.writeUint32(object.objectId || 0);
    writer.writeIntVector2(object.mapPosition || { x: 0, y: 0 });
    writer.writeQuat(object.rotation || { x: 0, y: 0, z: 0, w: 1 });
    const position = object.position || { x: 0, y: 0, z: 0 };
    writer.writeVector3({
      x: position.x * ROSE_POSITION_SCALE_OUT,
      y: position.y * ROSE_POSITION_SCALE_OUT,
      z: position.z * ROSE_POSITION_SCALE_OUT,
    });
    writer.writeVector3(object.scale || { x: 1, y: 1, z: 1 });
  }

  static parse(rh: BinaryReader): ZoneChunkData {
    const data = new ZoneChunkData();
    const blockCount = rh.readUint32();
    const headers: Array<{ type: number; offset: number }> = [];
    for (let index = 0; index < blockCount; index++) {
      headers.push({ type: rh.readUint32(), offset: rh.readUint32() });
    }

    const payloadEnd = (headerIndex: number): number => {
      const offset = headers[headerIndex].offset;
      let end = rh.byteLength;
      for (const header of headers) {
        if (header.offset > offset && header.offset < end) end = header.offset;
      }
      return end;
    };

    for (let blockIndex = 0; blockIndex < headers.length; blockIndex++) {
      const { type, offset } = headers[blockIndex];
      data.sourceBlockOrder.push(type);
      rh.seek(offset);

      if (type === BLOCK_TYPE.INFO) {
        data.rawBlocks.push({ type, data: rh.slice(offset, payloadEnd(blockIndex)) });
        continue;
      }

      if (type === BLOCK_TYPE.WATER_PATCH) {
        const object = ZoneChunkData.loadMapObject(rh, new ZoneWaterPatches());
        object.width  = rh.readUint32();
        object.height = rh.readUint32();

        for (let index = 0; index < object.width * object.height; ++index) {
          const patch = new WaterPatch();
          patch.hasWater = rh.readUint8() !== 0;
          patch.height   = rh.readFloat();
          patch.type     = rh.readUint32();
          patch.id       = rh.readUint32();
          patch.unknown  = rh.readUint32();
          object.patches[index] = patch;
        }
        data.waterPatches = object;
        continue;
      }

      if (!(type in IFO_COLLECTION_BY_BLOCK)) {
        data.rawBlocks.push({ type, data: rh.slice(offset, payloadEnd(blockIndex)) });
        continue;
      }

      if (type === BLOCK_TYPE.WATER_PLANE) {
        data.waterSize = rh.readFloat();
        (data.waterPlanes as any).waterSize = data.waterSize;
      }

      const objects = rh.readUint32();
      for (let objectIndex = 0; objectIndex < objects; ++objectIndex) {
        switch (type) {
        case BLOCK_TYPE.OBJECT:
          data.objects.push(ZoneChunkData.loadMapObject(rh, new ZoneObject()));
          break;
        case BLOCK_TYPE.NPC: {
          const object = ZoneChunkData.loadMapObject(rh, new ZoneNpc());
          object.aiIndex = rh.readUint32();
          object.conFilePath = rh.readUint8Str();
          data.npcs.push(object);
          break;
        }
        case BLOCK_TYPE.BUILDING:
          data.buildings.push(ZoneChunkData.loadMapObject(rh, new ZoneBuilding()));
          break;
        case BLOCK_TYPE.SOUND: {
          const object = ZoneChunkData.loadMapObject(rh, new ZoneSound());
          object.filePath = rh.readUint8Str();
          object.range = rh.readUint32();
          object.interval = rh.readUint32();
          data.sounds.push(object);
          break;
        }
        case BLOCK_TYPE.EFFECT: {
          const object = ZoneChunkData.loadMapObject(rh, new ZoneEffect());
          object.filePath = rh.readUint8Str();
          data.effects.push(object);
          break;
        }
        case BLOCK_TYPE.ANIMATION:
          data.animations.push(ZoneChunkData.loadMapObject(rh, new ZoneAnimation()));
          break;
        case BLOCK_TYPE.MONSTER_SPAWN: {
          const object = ZoneChunkData.loadMapObject(rh, new ZoneMonsterSpawn());
          object.name = rh.readUint8Str();

          let count = rh.readUint32();
          for (let index = 0; index < count; ++index) {
            const spawn = new MonsterSpawnEntry();
            spawn.name = rh.readUint8Str();
            spawn.monster = rh.readUint32();
            spawn.count = rh.readUint32();
            object.normal.push(spawn);
          }

          count = rh.readUint32();
          for (let index = 0; index < count; ++index) {
            const spawn = new MonsterSpawnEntry();
            spawn.name = rh.readUint8Str();
            spawn.monster = rh.readUint32();
            spawn.count = rh.readUint32();
            object.tactical.push(spawn);
          }

          object.interval = rh.readUint32();
          object.limit = rh.readUint32();
          object.range = rh.readUint32();
          object.tacticalPoints = rh.readUint32();
          data.monsterSpawns.push(object);
          break;
        }
        case BLOCK_TYPE.WATER_PLANE: {
          const object = new ZoneWaterPlane();
          object.start = rh.readVector3xzy();
          object.end = rh.readVector3xzy();
          data.waterPlanes.push(object);
          break;
        }
        case BLOCK_TYPE.WARP_POINT:
          data.warps.push(ZoneChunkData.loadMapObject(rh, new ZoneWarpPoint()));
          break;
        case BLOCK_TYPE.COLLISION_OBJECT:
          data.collisions.push(ZoneChunkData.loadMapObject(rh, new ZoneCollision()));
          break;
        case BLOCK_TYPE.EVENT_OBJECT: {
          const object = ZoneChunkData.loadMapObject(rh, new ZoneEvent());
          object.funcName = rh.readUint8Str();
          object.conFilePath = rh.readUint8Str();
          data.events.push(object);
          break;
        }
        }
      }
    }

    return data;
  }

  static serialize(data: ZoneChunkData): ArrayBuffer {
    const knownBlocks = new Map<number, Uint8Array>();
    const addObjectBlock = (
      type: number,
      objects: MapObject[],
      writeExtra?: (writer: BinaryWriter, object: any) => void,
    ) => {
      if (objects.length === 0 && !data.sourceBlockOrder.includes(type)) return;
      const writer = new BinaryWriter();
      writer.writeUint32(objects.length);
      for (const object of objects) {
        ZoneChunkData.writeMapObject(writer, object);
        if (writeExtra) writeExtra(writer, object);
      }
      knownBlocks.set(type, writer.toUint8Array());
    };

    addObjectBlock(BLOCK_TYPE.OBJECT, data.objects);
    addObjectBlock(BLOCK_TYPE.NPC, data.npcs, (writer, object: ZoneNpc) => {
      writer.writeUint32(object.aiIndex || 0);
      writer.writeUint8Str(object.conFilePath || '');
    });
    addObjectBlock(BLOCK_TYPE.BUILDING, data.buildings);
    addObjectBlock(BLOCK_TYPE.SOUND, data.sounds, (writer, object: ZoneSound) => {
      writer.writeUint8Str(object.filePath || '');
      writer.writeUint32(object.range || 0);
      writer.writeUint32(object.interval || 0);
    });
    addObjectBlock(BLOCK_TYPE.EFFECT, data.effects, (writer, object: ZoneEffect) => {
      writer.writeUint8Str(object.filePath || '');
    });
    addObjectBlock(BLOCK_TYPE.ANIMATION, data.animations);

    if (data.waterPatches) {
      const patchData = data.waterPatches;
      const expected = patchData.width * patchData.height;
      if (patchData.patches.length !== expected) {
        throw new Error(
          `Invalid IFO water patch dimensions: ${patchData.width}x${patchData.height} requires ${expected} patches, got ${patchData.patches.length}`,
        );
      }
      const writer = new BinaryWriter();
      ZoneChunkData.writeMapObject(writer, patchData);
      writer.writeUint32(patchData.width);
      writer.writeUint32(patchData.height);
      for (const patch of patchData.patches) {
        writer.writeUint8(patch.hasWater ? 1 : 0);
        writer.writeFloat(patch.height);
        writer.writeUint32(patch.type);
        writer.writeUint32(patch.id);
        writer.writeUint32(patch.unknown);
      }
      knownBlocks.set(BLOCK_TYPE.WATER_PATCH, writer.toUint8Array());
    }

    addObjectBlock(
      BLOCK_TYPE.MONSTER_SPAWN,
      data.monsterSpawns,
      (writer, object: ZoneMonsterSpawn) => {
        writer.writeUint8Str(object.name || '');
        writer.writeUint32(object.normal.length);
        for (const spawn of object.normal) {
          writer.writeUint8Str(spawn.name || '');
          writer.writeUint32(spawn.monster);
          writer.writeUint32(spawn.count);
        }
        writer.writeUint32(object.tactical.length);
        for (const spawn of object.tactical) {
          writer.writeUint8Str(spawn.name || '');
          writer.writeUint32(spawn.monster);
          writer.writeUint32(spawn.count);
        }
        writer.writeUint32(object.interval);
        writer.writeUint32(object.limit);
        writer.writeUint32(object.range);
        writer.writeUint32(object.tacticalPoints);
      },
    );

    if (
      data.waterPlanes.length > 0 ||
      data.sourceBlockOrder.includes(BLOCK_TYPE.WATER_PLANE)
    ) {
      const writer = new BinaryWriter();
      writer.writeFloat(data.waterSize || (data.waterPlanes as any).waterSize || 0);
      writer.writeUint32(data.waterPlanes.length);
      for (const plane of data.waterPlanes) {
        writer.writeVector3xzy(plane.start);
        writer.writeVector3xzy(plane.end);
      }
      knownBlocks.set(BLOCK_TYPE.WATER_PLANE, writer.toUint8Array());
    }

    addObjectBlock(BLOCK_TYPE.WARP_POINT, data.warps);
    addObjectBlock(BLOCK_TYPE.COLLISION_OBJECT, data.collisions);
    addObjectBlock(BLOCK_TYPE.EVENT_OBJECT, data.events, (writer, object: ZoneEvent) => {
      writer.writeUint8Str(object.funcName || '');
      writer.writeUint8Str(object.conFilePath || '');
    });

    const rawQueues = new Map<number, Uint8Array[]>();
    for (const block of data.rawBlocks) {
      const queue = rawQueues.get(block.type) || [];
      queue.push(block.data);
      rawQueues.set(block.type, queue);
    }

    const blocks: Array<{ type: number; payload: Uint8Array }> = [];
    const emittedKnown = new Set<number>();
    for (const type of data.sourceBlockOrder) {
      const raw = rawQueues.get(type)?.shift();
      if (raw) {
        blocks.push({ type, payload: raw });
      } else if (!emittedKnown.has(type) && knownBlocks.has(type)) {
        blocks.push({ type, payload: knownBlocks.get(type)! });
        emittedKnown.add(type);
      }
    }
    for (const [type, payload] of knownBlocks) {
      if (!emittedKnown.has(type)) blocks.push({ type, payload });
    }
    for (const [type, queue] of rawQueues) {
      for (const payload of queue) blocks.push({ type, payload });
    }

    const writer = new BinaryWriter();
    writer.writeUint32(blocks.length);
    const offsetFields: number[] = [];
    for (const block of blocks) {
      writer.writeUint32(block.type);
      offsetFields.push(writer.reserveUint32());
    }
    blocks.forEach((block, index) => {
      writer.patchUint32(offsetFields[index], writer.pos);
      writer.writeBytes(block.payload);
    });
    return writer.toArrayBuffer();
  }

  /**
   * @param {String} path
   * @param {Function} callback
   */
  static load(path: any, callback: any) {
    ROSELoader.load(path, function(rh: any) {
      callback(ZoneChunkData.parse(rh));
    });
  }
}

const IFO_COLLECTION_BY_BLOCK: Record<number, string> = {
  [BLOCK_TYPE.OBJECT]: 'objects',
  [BLOCK_TYPE.NPC]: 'npcs',
  [BLOCK_TYPE.BUILDING]: 'buildings',
  [BLOCK_TYPE.SOUND]: 'sounds',
  [BLOCK_TYPE.EFFECT]: 'effects',
  [BLOCK_TYPE.ANIMATION]: 'animations',
  [BLOCK_TYPE.MONSTER_SPAWN]: 'monsterSpawns',
  [BLOCK_TYPE.WATER_PLANE]: 'waterPlanes',
  [BLOCK_TYPE.WARP_POINT]: 'warps',
  [BLOCK_TYPE.COLLISION_OBJECT]: 'collisions',
  [BLOCK_TYPE.EVENT_OBJECT]: 'events',
};
